import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { isMopRequest } from "../helpers/requestFamily";
import { useRequestDetail } from "../hooks/useRequests";
import { useTenantConfig } from "../hooks/useTenantConfig";
import { useToast } from "../context/ToastContext";
import { useAuth } from "../auth/useAuth";
import { canWorkTickets } from "../auth/permissions";
import { MatchPanel, getConfirmableKeys } from "../components/MatchPanel";
import { ConfidenceBadge } from "../components/ConfidenceBadge";
import { ConfidenceExplainer } from "../components/ConfidenceExplainer";
import { RejectModal } from "../components/RejectModal";
import { ReprocessModal } from "../components/ReprocessModal";
import { PreviewModal, fileIcon, buildPreviewData, resolveContentType } from "../components/AttachmentPreview";
import type { PreviewData } from "../components/AttachmentPreview";
import { ConfirmModal } from "../components/ConfirmModal";
import { getOriginalSenderFull, getCleanSubject, getOriginalDate, getReceivedDate } from "../helpers/emailMetadata";
import { approveRequest, rejectRequest, revertRequest, reopenRequest, lockRequest, unlockRequest, unlockRequestBeacon, adminCostsByRequest, fetchAttachmentBlob } from "../api/client";
import { StatusBadge } from "../lib/status";
import { DetailPageShell } from "../lib/DetailPageShell";
import type { CorrectionItem, CostByRequestResponse, CostRequestCall, ProcessingRequest, IncomingData } from "../api/types";
import { stepColorScale, stepLabel } from "../lib/costSteps";

const LOCK_HEARTBEAT_MS = 2 * 60 * 1000; // 2 min

export function RequestDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { request, isLoading, error, refresh } = useRequestDetail(id);
  const { entityTypes, config } = useTenantConfig();
  const { addToast } = useToast();
  const { user } = useAuth();
  const isPlatformAdmin = user?.roles?.includes("platform_admin") ?? false;

  const redirectToMop = Boolean(request && request.id === id && config?.tenant_id === "casadei" && config.mop_enabled && isMopRequest(request));
  const lockReady = Boolean(request?.id === id && config);
  useEffect(() => {
    if (redirectToMop && request) {
      navigate(`/mop/requests/${request.id}`, { replace: true });
    }
  }, [redirectToMop, request, navigate]);

  const [costData, setCostData] = useState<CostByRequestResponse | null>(null);
  const [costLoading, setCostLoading] = useState(false);
  const [costOpen, setCostOpen] = useState(false);

  const loadCosts = useCallback(async () => {
    if (!id || !isPlatformAdmin) return;
    setCostLoading(true);
    try { setCostData(await adminCostsByRequest(id)); } catch { /* ignore */ }
    finally { setCostLoading(false); }
  }, [id, isPlatformAdmin]);

  useEffect(() => {
    if (costOpen && !costData && !costLoading) loadCosts();
  }, [costOpen, costData, costLoading, loadCosts]);

  const [corrections, setCorrections] = useState<CorrectionItem[]>([]);
  const [verdicts, setVerdicts] = useState<Record<string, "ok" | "corrected">>({});
  const [actionLoading, setActionLoading] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showReprocessModal, setShowReprocessModal] = useState(false);
  const [showRevertConfirm, setShowRevertConfirm] = useState(false);
  const [showReopenConfirm, setShowReopenConfirm] = useState(false);
  const [hasReprocessed, setHasReprocessed] = useState(false);
  const [editedMotivo, setEditedMotivo] = useState<string | null>(null);

  // Lock state
  const [lockOwner, setLockOwner] = useState<string | null>(null);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const lockIntervalRef = useRef<ReturnType<typeof setInterval>>();

  const hasCorrections = corrections.length > 0;
  const keyEntities = config?.confidence_key_entities ?? ["impianto"];

  // Lock on mount, heartbeat, cleanup
  useEffect(() => {
    if (!id || !lockReady || redirectToMop) return;
    let mounted = true;

    async function acquireLock() {
      try {
        const { status, data } = await lockRequest(id!);
        if (!mounted) return;
        if (status === 423) {
          setLockOwner(data.operator_name || data.locked_by || "altro operatore");
          setIsReadOnly(true);
        } else {
          setLockOwner(null);
          setIsReadOnly(false);
        }
      } catch {
        // ignore lock errors — don't block page
      }
    }

    acquireLock();
    lockIntervalRef.current = setInterval(acquireLock, LOCK_HEARTBEAT_MS);

    function handleBeforeUnload() {
      unlockRequestBeacon(id!);
    }
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      mounted = false;
      clearInterval(lockIntervalRef.current);
      window.removeEventListener("beforeunload", handleBeforeUnload);
      unlockRequest(id!).catch(() => {});
    };
  }, [id, lockReady, redirectToMop]);

  function handleCorrection(c: CorrectionItem) {
    setCorrections((prev) => {
      const filtered = prev.filter(
        (p) => !(p.ticket_index === c.ticket_index && p.field_corrected === c.field_corrected),
      );
      return [...filtered, c];
    });
    // If user modifies after reprocess, go back to state B
    if (hasReprocessed) setHasReprocessed(false);
  }

  function handleVerdict(entityKey: string, verdict: "ok" | "corrected" | undefined) {
    setVerdicts((prev) => {
      if (verdict === undefined) { const next = { ...prev }; delete next[entityKey]; return next; }
      return { ...prev, [entityKey]: verdict };
    });
  }

  async function handleApprove() {
    if (!id) return;
    setActionLoading(true);
    try {
      await approveRequest(id, "", motivoChanged ? { motivo: editedMotivo } : {});
      addToast("success", "Richiesta inviata alla coda");
      navigate("/");
    } catch (e) {
      addToast("error", `Errore: ${(e as Error).message}`);
    } finally {
      setActionLoading(false);
    }
  }

  async function handleReject(categories: string[], note: string) {
    if (!id) return;
    setActionLoading(true);
    try {
      await rejectRequest(id, { categories, note });
      addToast("success", "Richiesta scartata");
      navigate("/");
    } catch (e) {
      addToast("error", `Errore: ${(e as Error).message}`);
    } finally {
      setActionLoading(false);
      setShowRejectModal(false);
    }
  }

  async function handleRevert() {
    if (!id) return;
    setActionLoading(true);
    try {
      await revertRequest(id);
      addToast("info", "Match originale ripristinato");
      setHasReprocessed(false);
      setCorrections([]);
      setVerdicts({});
      refresh?.();
    } catch (e) {
      addToast("error", `Errore: ${(e as Error).message}`);
    } finally {
      setActionLoading(false);
    }
  }

  // Reopen: per done/auto_approved chiede conferma (cancella anche output Azure);
  // per discarded/error procede direttamente (non c'e' output da cancellare).
  function handleReopenClick() {
    if (!request) return;
    if (request.status === "done" || request.status === "auto_approved") {
      setShowReopenConfirm(true);
    } else {
      void doReopen();
    }
  }

  async function doReopen() {
    if (!id) return;
    setActionLoading(true);
    setShowReopenConfirm(false);
    try {
      const res = await reopenRequest(id);
      const purge = (res as any)?.output_purge;
      const purgedQ = purge?.queue_deleted ?? 0;
      const purgedB = purge?.blobs_deleted ?? 0;
      const errs = (purge?.errors ?? []).length;
      let msg = "Richiesta riaperta — puoi ora approvare o riprocessare";
      if (purgedQ > 0 || purgedB > 0) {
        msg = `Richiesta riaperta. Output rimosso dalla coda (${purgedQ} messaggi, ${purgedB} blob).`;
      }
      if (errs > 0) {
        msg += ` Attenzione: ${errs} errore/i durante la pulizia (verifica manualmente).`;
        addToast("error", msg);
      } else {
        addToast("info", msg);
      }
      refresh?.();
    } catch (e) {
      addToast("error", `Errore: ${(e as Error).message}`);
    } finally {
      setActionLoading(false);
    }
  }

  function handleReprocessComplete() {
    setHasReprocessed(true);
    setCorrections([]);
    setVerdicts({});
    setShowReprocessModal(false);
    refresh?.();
  }

  if (isLoading) {
    return <div className="loading-center" style={{ minHeight: 400 }}><div className="spinner spinner-lg" /></div>;
  }

  if (error || !request) {
    return (
      <div>
        <button className="btn btn-ghost" onClick={() => navigate(-1)}>{"←"} Indietro</button>
        <div className="card mt-4" style={{ background: "var(--indaco-danger-light)", color: "var(--indaco-danger)" }}>
          <span className="material-symbols-outlined" style={{ fontSize: 16, marginRight: 4 }}>error</span>
          {error || "Richiesta non trovata"}
        </div>
      </div>
    );
  }

  const incoming = request.incoming_data;
  const rawAnalyses = request.analyses;
  const analysis: Record<string, unknown> | null = Array.isArray(rawAnalyses)
    ? (rawAnalyses[0] as Record<string, unknown> ?? null)
    : (rawAnalyses as Record<string, unknown> | null);
  const extractedEntities = (analysis?.extracted_entities ?? null) as Record<string, Record<string, unknown> | null> | null;
  const ticket = request.matches?.[0] ?? null;
  const isActionable = request.status === "needs_review" || request.status === "pending_review" || request.status === "received";
  const isCompleted = request.status === "done" || request.status === "discarded" || request.status === "error" || request.status === "auto_approved";
  const userCanEdit = canWorkTickets(user?.roles ?? []);

  const originalMotivo = analysis?.motivo != null ? String(analysis.motivo) : "";
  const motivoChanged = editedMotivo !== null && editedMotivo !== originalMotivo;

  // Confirmation gate: only key entities (edificio) are mandatory; chiamante is optional
  const confirmableKeys = getConfirmableKeys(entityTypes, keyEntities);
  const missingConfirm = confirmableKeys.filter((k) => !verdicts[k]);
  const allConfirmed = missingConfirm.length === 0;

  // Email body fallback: when incoming body is empty (forwarded emails),
  // show the extracted text from the .eml attachment
  const extraction = request.extraction as Record<string, unknown> | null;
  const fallbackBody = (() => {
    if (incoming?.body_html || incoming?.body) return null; // has body, no fallback needed
    if (!extraction) return null;
    // Try body_clean first (just the email body text, no headers)
    const bodyClean = extraction.body_clean as string | undefined;
    if (bodyClean && bodyClean.trim().length > 5) return bodyClean;
    // Try attachments_text — find the .eml extracted text
    const attTexts = extraction.attachments_text as Array<{ filename?: string; method?: string; extracted_text?: string }> | undefined;
    if (attTexts) {
      const emlAtt = attTexts.find((a) => a.method === "extract_eml" || a.method === "extract_msg");
      if (emlAtt?.extracted_text) return emlAtt.extracted_text;
    }
    // Fallback: unified_text stripped of the FROM/SUBJECT header
    const ut = extraction.unified_text as string | undefined;
    if (ut) {
      // Remove the first 3 lines (FROM:, SUBJECT:, blank) which are the wrapper email headers
      const lines = ut.split("\n");
      const startIdx = lines.findIndex((l, i) => i >= 2 && l.trim() === "");
      return startIdx >= 0 ? lines.slice(startIdx + 1).join("\n").trim() : ut;
    }
    return null;
  })();

  // Merge allegati top-level con sub-allegati estratti dal .eml
  const topLevelAtts = incoming?.attachments ?? [];
  const extractionAttTexts = (extraction?.attachments_text ?? []) as Array<{
    filename:string; content_type?:string; size_bytes?:number; parent_eml?:string;
  }>;
  const subAtts = extractionAttTexts
    .filter(a => a.parent_eml)
    .map(a => ({ filename: a.filename, content_type: a.content_type || "application/octet-stream", size_bytes: a.size_bytes || 0 }));
  const visibleAttachments = [...topLevelAtts, ...subAtts];

  const canAct = isActionable && !isReadOnly && userCanEdit;

  return (
    <DetailPageShell
      onBack={() => navigate(-1)}
      backLabel="Indietro"
      title="Dettaglio richiesta"
      titleId={request.id}
      badges={<StatusBadge status={request.status} />}
      approve={canAct && !hasCorrections ? {
        onClick: handleApprove,
        disabled: actionLoading || !allConfirmed,
        label: `Invia alla coda${motivoChanged ? " (motivo)" : ""}`,
        title: allConfirmed ? "" : `Conferma prima: ${missingConfirm.map((k) => entityTypes.find((e) => e.key === k)?.label ?? k).join(", ")}`,
      } : undefined}
      reprocess={canAct && hasCorrections ? {
        onClick: () => setShowReprocessModal(true),
        disabled: actionLoading,
        label: `Rielabora (${corrections.length})`,
      } : undefined}
      reject={canAct ? { onClick: () => setShowRejectModal(true), disabled: actionLoading, label: "Scarta" } : undefined}
      readOnlyMessage={isReadOnly && lockOwner ? (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
          <span className="material-symbols-outlined" style={{ fontSize: 14 }}>lock</span>
          In lavorazione da <strong>{lockOwner}</strong>
        </span>
      ) : undefined}
      extraActions={canAct ? (
        <>
          {hasReprocessed && !hasCorrections && (
            <button className="btn btn-outline btn-sm" disabled={actionLoading} onClick={() => setShowRevertConfirm(true)}>
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>undo</span> Revert
            </button>
          )}
          {!allConfirmed && !hasCorrections && (
            <span style={{ fontSize: 12, color: "var(--indaco-warning)", display: "inline-flex", alignItems: "center", gap: 4 }}>
              <span className="material-symbols-outlined" style={{ fontSize: 14 }}>warning</span>
              Conferma {missingConfirm.map((k) => entityTypes.find((e) => e.key === k)?.label ?? k).join(", ")}
            </span>
          )}
        </>
      ) : undefined}
    >
      <div className="detail-shell__scroll" style={{ overflowY: "auto", overflowX: "hidden", flex: 1, minHeight: 0, paddingRight: 2 }}>

      {/* Reopen bar — for completed requests */}
      {isCompleted && !isReadOnly && userCanEdit && (
        <div style={{
          padding: "var(--space-3)", marginBottom: "var(--space-3)",
          background: request.status === "discarded" ? "var(--indaco-warning-light)" : "var(--indaco-info-light)",
          borderRadius: "var(--border-radius-md)",
          border: `1px solid ${request.status === "discarded" ? "var(--indaco-warning)" : "var(--indaco-info)"}`,
          display: "flex", alignItems: "center", gap: "var(--space-3)", flexWrap: "wrap",
        }}>
          <button className="btn btn-primary" disabled={actionLoading} onClick={handleReopenClick}>
            <span className="material-symbols-outlined" style={{ fontSize: 18, marginRight: 4 }}>
              {request.status === "discarded" ? "replay" : "undo"}
            </span>
            {request.status === "done" || request.status === "auto_approved"
              ? "Riapri richiesta" : request.status === "discarded" ? "Rimetti in lavorazione" : "Riprova lavorazione"}
          </button>
          <span className="text-sm text-muted">
            {request.status === "done" || request.status === "auto_approved"
              ? "Riporta in lavorazione per re-invio o modifica"
              : request.status === "discarded" ? "Non è mai stata inviata alla coda" : "Riporta in lavorazione"}
          </span>
        </div>
      )}

      <div style={panelsStyle}>
        {/* LEFT (60%): analysis + matching — work area */}
        <div style={{ minWidth: 0 }}>
          {analysis && (
            <div className="card" style={{ marginBottom: "var(--space-3)", borderLeft: "4px solid var(--indaco-brand-primary)" }}>
              <div className="text-sm fw-semibold mb-2">
                <span className="material-symbols-outlined" style={{ fontSize: 16, marginRight: 4 }}>smart_toy</span> Analisi
              </div>
              <div style={metaGridStyle}>
                {analysis.tipologia != null && (<React.Fragment><span className="text-muted fw-medium">Tipologia:</span><span>{String(analysis.tipologia)}</span></React.Fragment>)}
                {analysis.priorita != null && (<React.Fragment><span className="text-muted fw-medium">Priorità:</span><span>{String(analysis.priorita)}</span></React.Fragment>)}
              </div>
              {(analysis.motivo != null || isActionable) && (
                <div style={{ marginTop: "var(--space-2)" }}>
                  <label className="form-label">Motivo:</label>
                  {isActionable && !isReadOnly && userCanEdit ? (
                    <div style={{ position: "relative" }}>
                      <textarea className="form-textarea"
                        style={{ width: "100%", minHeight: 60, fontSize: "var(--font-size-sm)" }}
                        value={editedMotivo ?? originalMotivo}
                        onChange={(e) => setEditedMotivo(e.target.value)} />
                      {motivoChanged && (
                        <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", marginTop: "var(--space-1)" }}>
                          <span className="text-xs" style={{ color: "var(--indaco-warning)" }}>
                            <span className="material-symbols-outlined" style={{ fontSize: 14, verticalAlign: "middle", marginRight: 2 }}>edit</span>
                            Modificato — verrà salvato con l{"'"}approvazione
                          </span>
                          <button className="btn btn-ghost btn-xs" onClick={() => setEditedMotivo(null)}>
                            <span className="material-symbols-outlined" style={{ fontSize: 14 }}>undo</span> Ripristina
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <span className="text-sm">{String(analysis.motivo ?? "")}</span>
                  )}
                </div>
              )}
            </div>
          )}

          {ticket && (
            <ConfidenceExplainer ticket={ticket} entityTypes={entityTypes} keyEntities={keyEntities} />
          )}

          {ticket ? (
            <MatchPanel ticket={ticket} entityTypes={entityTypes} keyEntities={keyEntities} extractedEntities={extractedEntities ?? undefined}
              verdicts={verdicts} onVerdict={handleVerdict} onCorrection={handleCorrection}
              readOnly={isCompleted || isReadOnly || !userCanEdit}
              onResetAll={() => { setCorrections([]); setVerdicts({}); }}
              onCascadeReset={(keys) => setVerdicts((prev) => {
                const next = { ...prev };
                for (const k of keys) delete next[k];
                return next;
              })} />
          ) : (
            <div className="card text-center text-muted" style={{ padding: "var(--space-10)" }}>Nessun risultato di matching disponibile</div>
          )}

          {/* Changes summary is now inside MatchPanel (shows cascade diffs too) */}
        </div>

        {/* RIGHT (40%): email context — stretches to match left column */}
        <div style={{ minWidth: 0, display: "flex", flexDirection: "column" }}>
          <EmailSidebar
            request={request}
            incoming={incoming}
            fallbackBody={fallbackBody}
            requestId={id!}
            attachments={visibleAttachments}
            extractedEntities={extractedEntities}
            entityTypes={entityTypes}
          />
        </div>
      </div>

      {/* Reject modal */}
      {showRejectModal && (
        <RejectModal
          loading={actionLoading}
          onConfirm={handleReject}
          onCancel={() => setShowRejectModal(false)}
        />
      )}

      {/* Reprocess modal */}
      {showReprocessModal && id && (
        <ReprocessModal
          requestId={id}
          corrections={corrections}
          operatorOverrides={motivoChanged ? { motivo: editedMotivo ?? "" } : undefined}
          onComplete={handleReprocessComplete}
          onCancel={() => setShowReprocessModal(false)}
        />
      )}

      {/* Revert confirmation modal */}
      {showRevertConfirm && (
        <ConfirmModal
          title="Conferma revert"
          message={
            <p style={{ margin: 0 }}>
              Vuoi ripristinare i match originali e annullare la rielaborazione?
              Le correzioni precedenti verranno annullate.
            </p>
          }
          confirmLabel="Ripristina originali"
          cancelLabel="Annulla"
          onConfirm={() => { setShowRevertConfirm(false); handleRevert(); }}
          onCancel={() => setShowRevertConfirm(false)}
          loading={actionLoading}
        />
      )}

      {/* Reopen confirmation modal — solo per done/auto_approved (cancella output Azure) */}
      {showReopenConfirm && (
        <ConfirmModal
          title="Riaprire la richiesta?"
          message={
            <>
              <p style={{ margin: "0 0 var(--space-2)" }}>
                La richiesta tornerà in <strong>lavorazione</strong> e potrai modificarla o approvarla di nuovo.
              </p>
              <p style={{ margin: "0 0 var(--space-2)" }}>
                Verrà <strong>rimossa dalla coda di output</strong> verso il gestionale Casadei: il messaggio in coda
                e i file caricati su Azure Blob saranno cancellati per evitare doppioni.
              </p>
              <p style={{ margin: 0, fontStyle: "italic" }}>
                Se il messaggio è già stato letto e processato dal gestionale, la rimozione potrebbe non avere effetto a valle.
              </p>
            </>
          }
          confirmLabel="Riapri e svuota coda"
          cancelLabel="Annulla"
          onConfirm={() => void doReopen()}
          onCancel={() => setShowReopenConfirm(false)}
          loading={actionLoading}
        />
      )}

      {/* Previous corrections */}
      {request.corrections && request.corrections.length > 0 && (
        <div className="card" style={{ marginTop: "var(--space-4)" }}>
          <div className="fw-semibold mb-4">Storico correzioni</div>
          <div className="table-container">
            <table>
              <thead><tr><th>Campo</th><th>Categoria</th><th>Nota</th><th>Data</th></tr></thead>
              <tbody>
                {request.corrections.map((c) => (
                  <tr key={c.id} style={{ cursor: "default" }}>
                    <td>{c.field_corrected}</td>
                    <td><span className="badge badge-neutral">{c.correction_category}</span></td>
                    <td className="text-sm">{c.correction_note || "—"}</td>
                    <td className="text-sm">{formatDate(c.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* LLM Costs */}
      {isPlatformAdmin && (
        <div className="card" style={{ marginTop: "var(--space-4)" }}>
          <button className="btn-ghost btn-sm" onClick={() => setCostOpen((o) => !o)}
            style={{ display: "flex", alignItems: "center", gap: 6, width: "100%", fontWeight: 600 }}>
            <span className="material-symbols-outlined" style={{ fontSize: 18, transition: "transform 0.2s", transform: costOpen ? "rotate(90deg)" : "rotate(0)" }}>chevron_right</span>
            <span className="material-symbols-outlined" style={{ fontSize: 18, color: "var(--indaco-brand-primary)" }}>payments</span>
            Costi LLM
            {costData && (<span className="badge badge-neutral" style={{ marginLeft: 8, fontWeight: 400 }}>
              {costData.total_cost_usd == null || costData.priced_call_count === 0 ? "costo n/d" : fmtUsd(costData.total_cost_usd)} {"·"} {costData.calls.length} chiamate
            </span>)}
          </button>
          {costOpen && (
            <div style={{ marginTop: "var(--space-4)" }}>
              {costLoading ? (<div style={{ textAlign: "center", padding: "var(--space-4)" }}><span className="spinner" /></div>)
                : costData && costData.calls.length > 0 ? (<RequestCostTable calls={costData.calls} totalCost={costData.total_cost_usd} pricedCount={costData.priced_call_count} />)
                : (<div className="text-sm text-muted" style={{ padding: "var(--space-2) 0" }}>Nessuna chiamata LLM registrata per questa richiesta.</div>)}
            </div>
          )}
        </div>
      )}

      {/* Link to audit log */}
      <div style={{ marginTop: "var(--space-4)", textAlign: "center" }}>
        <Link to={`/audit?request_id=${request.id}`} className="btn btn-ghost btn-sm text-muted">
          <span className="material-symbols-outlined" style={{ fontSize: 16, marginRight: 4 }}>history</span>
          Vedi storico operazioni
        </Link>
      </div>
      </div>
    </DetailPageShell>
  );
}

function ExtractionEditor({ extractedEntities, entityTypes, loading, onSave, onCancel }: {
  extractedEntities: Record<string, Record<string, unknown> | null>;
  entityTypes: { key: string; label: string }[];
  loading: boolean;
  onSave: (edited: Record<string, Record<string, unknown>>) => void;
  onCancel: () => void;
}) {
  const [edited, setEdited] = useState<Record<string, Record<string, string>>>(() => {
    const init: Record<string, Record<string, string>> = {};
    for (const [key, fields] of Object.entries(extractedEntities)) {
      if (fields) {
        init[key] = {};
        for (const [fk, fv] of Object.entries(fields)) {
          init[key][fk] = fv != null ? String(fv) : "";
        }
      }
    }
    return init;
  });

  function updateField(entityKey: string, fieldKey: string, value: string) {
    setEdited((prev) => ({ ...prev, [entityKey]: { ...prev[entityKey], [fieldKey]: value } }));
  }

  return (
    <div style={{ background: "var(--indaco-warning-light)", padding: "var(--space-4)", borderRadius: "var(--border-radius-md)", borderLeft: "4px solid var(--indaco-warning)" }}>
      <div className="fw-semibold mb-3">
        <span className="material-symbols-outlined" style={{ fontSize: 18, marginRight: 4 }}>tune</span>
        Correggi estrazione LLM
      </div>
      <div className="text-xs text-muted mb-3">
        {"Modifica i campi estratti dal sistema. Dopo il salvataggio il matching verrà rieseguito con i nuovi dati."}
      </div>
      {Object.entries(edited).map(([entityKey, fields]) => {
        const et = entityTypes.find((e) => e.key === entityKey);
        const label = et?.label ?? entityKey;
        return (
          <div key={entityKey} style={{ marginBottom: "var(--space-3)" }}>
            <div className="text-sm fw-semibold mb-2">{label}</div>
            <div style={{ display: "grid", gridTemplateColumns: "140px 1fr", gap: "var(--space-1) var(--space-2)", alignItems: "center" }}>
              {Object.entries(fields).map(([fk, fv]) => (
                <React.Fragment key={fk}>
                  <label className="text-xs text-muted fw-medium">{fk.replace(/_/g, " ")}:</label>
                  <input className="form-input" style={{ fontSize: "var(--font-size-sm)" }}
                    value={fv} onChange={(e) => updateField(entityKey, fk, e.target.value)} />
                </React.Fragment>
              ))}
            </div>
          </div>
        );
      })}
      <div style={{ display: "flex", gap: "var(--space-2)", marginTop: "var(--space-3)" }}>
        <button className="btn btn-warning" disabled={loading} onClick={() => onSave(edited)}>
          <span className="material-symbols-outlined" style={{ fontSize: 18, marginRight: 4 }}>refresh</span> Salva e Riprocessa
        </button>
        <button className="btn btn-outline" onClick={onCancel}>Annulla</button>
      </div>
    </div>
  );
}

/** Extract a human-readable name from a correction value ({pk, descrizione?, nome?, ...}). */
function correctionDisplayName(val: Record<string, unknown> | null): string {
  if (!val) return "";
  const name = val.descrizione ?? val.nome ?? val.ragione_sociale ?? val.nominativo ?? val.desc_tipo ?? val.display_label;
  const pk = val.pk ? String(val.pk) : "";
  if (name) return pk ? `${String(name)} (${pk})` : String(name);
  return pk;
}

// StatusBadge ora importato da lib/status (registry unico)

function formatDate(iso: string) {
  try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Etichette/colori degli step: mappa unica in lib/costSteps (condivisa con AdminCosts).

/** Costo/latenza possono essere null ("non lo sappiamo"): mai renderli come 0. */
const NO_DATA = "—";
// Locale italiano: virgola per i decimali, non il punto — mai `toFixed` grezzo.
const _fmtEurCache = new Map<number, Intl.NumberFormat>();
function _fmtEur(digits: number): Intl.NumberFormat {
  let f = _fmtEurCache.get(digits);
  if (!f) { f = new Intl.NumberFormat("it-IT", { minimumFractionDigits: digits, maximumFractionDigits: digits }); _fmtEurCache.set(digits, f); }
  return f;
}

function fmtUsd(v: number | null | undefined, digits = 2) {
  if (v == null) return NO_DATA;
  // Un costo reale (>0) che arrotonda a "0,00" sembra gratis: si espande la
  // precisione invece di mentire (frequente per l'OCR a pagina). Stessa
  // logica di AdminCosts.tsx.
  if (v > 0 && Number(v.toFixed(digits)) === 0) return `€${_fmtEur(4).format(v)}`;
  return `€${_fmtEur(digits).format(v)}`;
}
function fmtMs(v: number | null | undefined) {
  return v == null ? NO_DATA : `${v.toFixed(0)} ms`;
}
/** I token possono mancare (travaso deleghe): `null.toLocaleString()` farebbe crashare la pagina. */
function fmtTok(v: number | null | undefined) {
  return v == null ? NO_DATA : v.toLocaleString("it-IT");
}

function RequestCostTable({ calls, totalCost, pricedCount }: { calls: CostRequestCall[]; totalCost: number | null; pricedCount?: number }) {
  const withTokens = calls.filter((c) => c.total_tokens != null);
  const totalTokens = withTokens.length > 0 ? withTokens.reduce((s, c) => s + (c.total_tokens ?? 0), 0) : null;
  const latencies = calls.map((c) => c.latency_ms).filter((v): v is number => v != null);
  const avgLatency = latencies.length > 0 ? latencies.reduce((s, v) => s + v, 0) / latencies.length : null;
  const unpricedCalls = calls.filter((c) => c.cost_usd == null);
  const unpricedTokens = unpricedCalls.reduce((s, c) => s + (c.total_tokens ?? 0), 0);
  const colorOf = stepColorScale(calls.map((c) => c.step));
  // Nessuna chiamata prezzata = costo ignoto, anche se l'API emettesse 0.
  const shownTotal = pricedCount === 0 || unpricedCalls.length === calls.length ? null : totalCost;
  return (
    <div className="table-container" style={{ margin: 0 }}>
      {/* `.truncate` = white-space nowrap ereditato dalle celle: a 390px la tabella
          scorre in `.table-container` invece di spezzare "Trascrizione" lettera per
          lettera (`.card` ha overflow-wrap: anywhere). Overflow/ellipsis sono inerti su un table box. */}
      <table className="truncate">
        <thead><tr><th>Step</th><th>Tipo</th><th>Modello</th><th style={{ textAlign: "right" }}>Prompt</th><th style={{ textAlign: "right" }}>Completion</th><th style={{ textAlign: "right" }}>Totale tok.</th><th style={{ textAlign: "right" }}>Costo (€)</th><th style={{ textAlign: "right" }}>Latenza</th></tr></thead>
        <tbody>
          {calls.map((c) => (
            <tr key={c.id} style={{ cursor: "default" }}>
              <td><span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: colorOf(c.step), marginRight: 6, verticalAlign: "middle" }} />{stepLabel(c.step)}</td>
              <td>{c.call_type}</td>
              <td>{c.model == null ? <span className="text-muted">{NO_DATA}</span> : <code style={{ fontSize: "var(--font-size-xs)" }}>{c.model}</code>}</td>
              <td style={{ textAlign: "right" }}>{fmtTok(c.prompt_tokens)}</td>
              <td style={{ textAlign: "right" }}>{fmtTok(c.completion_tokens)}</td>
              <td style={{ textAlign: "right" }}>{fmtTok(c.total_tokens)}</td>
              <td style={{ textAlign: "right" }} className={c.cost_usd == null ? "text-muted" : undefined} title={c.cost_usd == null ? "Costo non disponibile: prezzo del modello mancante" : undefined}>{fmtUsd(c.cost_usd)}</td>
              <td style={{ textAlign: "right" }} className={c.latency_ms == null ? "text-muted" : undefined}>{fmtMs(c.latency_ms)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ fontWeight: 600, borderTop: "2px solid var(--indaco-gray-300)" }}>
            <td colSpan={5} style={{ textAlign: "right" }}>Totale</td>
            <td style={{ textAlign: "right" }}>{fmtTok(totalTokens)}</td>
            <td style={{ textAlign: "right" }} className={shownTotal == null ? "text-muted" : undefined} title={shownTotal == null ? "Costo non disponibile: nessuna chiamata con prezzo" : undefined}>{fmtUsd(shownTotal)}</td>
            <td style={{ textAlign: "right" }}>{avgLatency == null ? NO_DATA : `${avgLatency.toFixed(0)} ms (avg)`}</td>
          </tr>
        </tfoot>
      </table>
      {unpricedCalls.length > 0 && (
        <div className="text-sm text-muted" style={{ padding: "var(--space-2) var(--space-3)" }}>
          <span className="material-symbols-outlined" style={{ fontSize: 16, verticalAlign: "text-bottom", marginRight: 4, color: "var(--indaco-warning)" }}>info</span>
          Il totale copre {calls.length - unpricedCalls.length} chiamate su {calls.length}: per {unpricedCalls.length} manca il prezzo del modello
          ({unpricedTokens.toLocaleString("it-IT")} token non valorizzati).
        </div>
      )}
    </div>
  );
}

/* ── Attachment Section ── */

function AttachmentSection({ requestId, attachments }: { requestId: string; attachments: { filename: string; content_type: string; size_bytes: number }[] }) {
  const [previewing, setPreviewing] = React.useState<PreviewData | null>(null);
  const [loading, setLoading] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [renderError, setRenderError] = React.useState<string | null>(null);

  async function handleClick(att: { filename: string; content_type: string }) {
    setError(null);
    setRenderError(null);
    setLoading(att.filename);
    try {
      const { url, contentType: rawCT } = await fetchAttachmentBlob(requestId, att.filename);
      const { preview, renderError: re } = await buildPreviewData(url, rawCT, att.filename);
      if (re) setRenderError(re);
      setPreviewing(preview);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Errore download";
      if (msg.includes("404")) setError("Allegato non disponibile — email processata prima del salvataggio allegati. Riprocessare per recuperarlo.");
      else setError(msg);
    } finally {
      setLoading(null);
    }
  }

  function closePreview() {
    if (previewing) URL.revokeObjectURL(previewing.url);
    setPreviewing(null);
    setRenderError(null);
  }

  return (
    <div style={{ marginTop: "var(--space-4)" }}>
      <div className="text-sm fw-semibold mb-2">
        <span className="material-symbols-outlined" style={{ fontSize: 16, marginRight: 4 }}>attach_file</span> Allegati ({attachments.length})
      </div>
      <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap" }}>
        {attachments.map((a, i) => (
          <button key={i} className="btn btn-outline btn-sm"
            style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: "var(--font-size-xs)", maxWidth: "100%", overflow: "hidden" }}
            disabled={loading === a.filename}
            onClick={() => handleClick(a)}>
            <span className="material-symbols-outlined" style={{ fontSize: 16, flexShrink: 0 }}>{fileIcon(a.content_type, a.filename)}</span>
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>{a.filename}</span>
            <span className="text-muted" style={{ flexShrink: 0 }}>({formatBytes(a.size_bytes)})</span>
            {loading === a.filename && <span className="spinner" style={{ width: 14, height: 14, flexShrink: 0 }} />}
          </button>
        ))}
      </div>
      {error && <div className="text-sm" style={{ color: "var(--indaco-danger)", marginTop: "var(--space-2)" }}>{error}</div>}
      {previewing && <PreviewModal data={previewing} renderError={renderError} onClose={closePreview} />}
    </div>
  );
}


/* ── Email Sidebar (right panel) ── */

function collapseLines(t: string): string {
  return t.replace(/&nbsp;/gi, ' ').replace(/&amp;/gi, '&').replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>').replace(/&quot;/gi, '"').replace(/&#(\d+);/g, (_, n) => String.fromCharCode(+n))
    .replace(/\r\n/g, '\n').replace(/\r/g, '\n')
    .split('\n').map(l => l.trim()).filter(l => l.length > 0).join('\n').trim();
}

function EmailSidebar({ request, incoming, fallbackBody, requestId, attachments, extractedEntities, entityTypes }: {
  request: ProcessingRequest;
  incoming: IncomingData | null;
  fallbackBody: string | null;
  requestId: string;
  attachments: { filename: string; content_type: string; size_bytes: number }[];
  extractedEntities: Record<string, Record<string, unknown> | null> | null;
  entityTypes: { key: string; label: string }[];
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Email metadata + body — flex:1 to fill available space */}
      <div className="card" style={{ marginBottom: "var(--space-3)", flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
        <div className="text-sm fw-semibold mb-2" style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
          <span className="material-symbols-outlined" style={{ fontSize: 16 }}>mail</span> Email originale
        </div>
        <div style={{ ...sidebarMetaStyle, flexShrink: 0 }}>
          <span className="text-muted fw-medium">Da:</span>
          <span className="text-sm">{getOriginalSenderFull(request) || "—"}</span>
          <span className="text-muted fw-medium">Oggetto:</span>
          <span className="text-sm fw-semibold">{getCleanSubject(request) || "—"}</span>
          <span className="text-muted fw-medium">Data:</span>
          <span className="text-sm">{(() => { const d = getOriginalDate(request); return d ? formatDate(d) : "—"; })()}</span>
        </div>
        {/* Body — fills remaining card space, scrolls if needed */}
        {(() => {
          const extractionHtml = (request.extraction as Record<string, unknown> | null)?.body_html as string | null;
          const displayHtml = incoming?.body_html || extractionHtml || null;
          return (
            <div style={{ marginTop: "var(--space-2)", flex: 1, minHeight: 80, display: "flex", flexDirection: "column" }}>
              {displayHtml ? (
                <iframe srcDoc={displayHtml} sandbox="" title="Email"
                  style={{ width: "100%", flex: 1, minHeight: 80, border: "1px solid var(--indaco-gray-200)", borderRadius: "var(--border-radius-sm)", background: "var(--indaco-white)" }} />
              ) : (
                <div style={{
                  padding: "8px 10px", background: "var(--indaco-gray-50)", borderRadius: 6,
                  whiteSpace: "pre-wrap", fontSize: 12, lineHeight: 1.55, color: "var(--indaco-gray-800)",
                  flex: 1, overflowY: "auto", minHeight: 80,
                }}>
                  {(() => {
                    const raw = incoming?.body || fallbackBody;
                    return raw ? collapseLines(raw) : <span className="text-muted">Corpo non disponibile</span>;
                  })()}
                </div>
              )}
            </div>
          );
        })()}
        {fallbackBody && !incoming?.body_html && !incoming?.body && !(request.extraction as Record<string, unknown> | null)?.body_html && (
          <div className="text-xs text-muted" style={{ marginTop: "var(--space-1)", flexShrink: 0 }}>
            <span className="material-symbols-outlined" style={{ fontSize: 14, verticalAlign: "middle", marginRight: 2 }}>info</span>
            Estratto dall{"'"}email originale inoltrata
          </div>
        )}
      </div>

      {/* Attachments */}
      {attachments.length > 0 && (
        <div className="card" style={{ marginBottom: "var(--space-3)", flexShrink: 0 }}>
          <AttachmentSection requestId={requestId} attachments={attachments} />
        </div>
      )}

      {/* Extracted entities */}
      {extractedEntities && Object.keys(extractedEntities).length > 0 && (
        <div className="card" style={{ marginBottom: "var(--space-3)", flexShrink: 0 }}>
          <div className="text-sm fw-semibold mb-2" style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>search</span> Estratto dall{"'"}email
          </div>
          {Object.entries(extractedEntities).map(([entityKey, entityFields]) => {
            if (!entityFields || Object.keys(entityFields).length === 0) return null;
            const et = entityTypes.find((e) => e.key === entityKey);
            const label = et?.label ?? entityKey;
            const values = Object.entries(entityFields).filter(([, v]) => v != null && String(v).trim() !== "").map(([, v]) => String(v)).join(", ");
            if (!values) return null;
            return (<div key={entityKey} className="text-sm" style={{ marginBottom: "var(--space-1)" }}><span className="text-muted fw-medium">{label}:</span>{" "}<span>{values}</span></div>);
          })}
        </div>
      )}
    </div>
  );
}

const sidebarMetaStyle: React.CSSProperties = { display: "grid", gridTemplateColumns: "auto minmax(0, 1fr)", gap: "2px var(--space-2)", fontSize: "var(--font-size-xs)" };

const panelsStyle: React.CSSProperties = { display: "grid", gridTemplateColumns: "minmax(0, 3fr) minmax(0, 2fr)", gap: "var(--space-4)", alignItems: "stretch" };
const metaGridStyle: React.CSSProperties = { display: "grid", gridTemplateColumns: "auto minmax(0, 1fr)", gap: "var(--space-1) var(--space-3)", fontSize: "var(--font-size-sm)" };
